import { notify } from '@affine/component';
import { DeleteIcon } from '@blocksuite/icons/rc';
import { createElement } from 'react';

import * as styles from './notify-moved-to-trash.css';

const MOVED_TO_TRASH_TOAST_DURATION_MS = 4000;

/**
 * Shared "moved to trash" toast with two actions: Undo and Open trash.
 *
 * Soft-delete in StreamLain is silent — deleted database/automation blocks land
 * in the workspace trash (sidebar) and deleted docs land in the docs trash, but
 * nothing tells the user where it went or how to get it back. This toast makes
 * that discoverable and recoverable. i18n is resolved by the caller so this
 * helper stays surface-agnostic (canvas blocks + docs).
 */
export interface MovedToTrashToastOptions {
  /** Localized title, e.g. "Database moved to trash". */
  title: string;
  /** Localized label for the undo action. */
  undoLabel: string;
  /** Localized label for the open-trash action. */
  openTrashLabel: string;
  /** Restore the just-deleted item. */
  onUndo: () => void | Promise<void>;
  /** Reveal the relevant trash location (sidebar trash / docs trash). */
  onOpenTrash: () => void;
}

export function notifyMovedToTrash({
  title,
  undoLabel,
  openTrashLabel,
  onUndo,
  onOpenTrash,
}: MovedToTrashToastOptions): void {
  notify(
    {
      title,
      message: createElement(
        'span',
        {
          'aria-hidden': true,
          className: styles.progressShell,
        },
        createElement('span', { className: styles.progress })
      ),
      icon: createElement(DeleteIcon),
      actions: [
        {
          key: 'undo',
          label: undoLabel,
          autoClose: true,
          onClick: () => {
            void onUndo();
          },
        },
        {
          key: 'open-trash',
          label: openTrashLabel,
          autoClose: true,
          onClick: () => {
            onOpenTrash();
          },
        },
      ],
    },
    { duration: MOVED_TO_TRASH_TOAST_DURATION_MS }
  );
}
